/* Milliard theme — product page behavior.
   variant-picker: option state → variant resolution → price/media/ATC/URL sync
   media-gallery: carousel + thumbs + zoom
   sticky-atc: mobile bar past the buy box
   product-recommendations / recently-viewed loaders */
(() => {
  'use strict';

  const productJsonEl = document.querySelector('[data-product-json]');
  let productData = null;
  try {
    productData = productJsonEl ? JSON.parse(productJsonEl.textContent) : null;
  } catch (e) {
    console.error('Milliard theme: invalid product JSON', e); // leave the page on its server-rendered state
  }
  const money = (cents) => {
    if (cents === null || cents === undefined) return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: window.MBTheme?.currency || 'USD' }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  /* ---------- <variant-picker> ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      if (!productData) return;
      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-option-value]')) this.onChange();
      });
      this.onChange(false);
    }

    selectedOptions() {
      return [...this.querySelectorAll('fieldset')].map((fs) => fs.querySelector('input:checked')?.value ?? null);
    }

    currentVariant() {
      const selected = this.selectedOptions();
      return productData.variants.find((v) => v.options.every((val, i) => val === selected[i])) || null;
    }

    onChange(notify = true) {
      const selected = this.selectedOptions();
      const variant = this.currentVariant();

      /* contextual availability: for each option value, is there an available
         variant matching the OTHER currently selected options? */
      this.querySelectorAll('fieldset').forEach((fs, position) => {
        const label = fs.querySelector('[data-selected-value]');
        if (label && selected[position]) label.textContent = selected[position];
        fs.querySelectorAll('input[data-option-value]').forEach((input) => {
          if (input.dataset.valueSoldout === 'true') return; // hard sold-out everywhere
          const hypothetical = [...selected];
          hypothetical[position] = input.value;
          const match = productData.variants.find((v) => v.options.every((val, i) => hypothetical[i] === null || val === hypothetical[i]));
          const combinationAvailable = productData.variants.some(
            (v) => v.available && v.options.every((val, i) => (i === position ? val === input.value : hypothetical[i] === null || val === hypothetical[i]))
          );
          const valueLabel = fs.querySelector(`label[for="${input.id}"]`);
          if (valueLabel) {
            const unavailable = !!match && !combinationAvailable;
            valueLabel.classList.toggle('variant-option__value--unavailable', unavailable);
            const note = valueLabel.querySelector('[data-unavailable-note]');
            if (note) note.hidden = !unavailable;
          }
        });
      });

      this.updateBuyArea(variant);
      if (variant) {
        this.updateMedia(variant);
        this.updateUrl(variant);
      }
      if (notify) {
        document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant, product: productData } }));
      }
    }

    updateBuyArea(variant) {
      /* Scope to this picker's section: document-wide queries would clobber
         the first card quick-add form if one ever renders above the buy box. */
      const scope = this.closest('.shopify-section') || document;
      const purchaseArea = scope.querySelector('[data-purchase-area]');
      const soldoutPanel = scope.querySelector('[data-soldout-panel]');
      const button = scope.querySelector('[data-atc-button]');
      const idInput = scope.querySelector('[data-variant-id]');
      const dynamic = scope.querySelector('[data-dynamic-checkout]');
      const stickyBtn = scope.querySelector('[data-sticky-submit]');
      const qtyBlock = scope.querySelector('[data-quantity-block]');
      const priceWrap = scope.querySelector('[data-price-wrapper]');
      const stickyPrice = scope.querySelector('[data-sticky-price]');
      const strings = window.MBTheme?.strings || {};

      const available = !!(variant && variant.available);

      /* swap the whole add-to-cart form for the notify panel (and back) so any
         colour — sold out or not — lands the shopper on the right state for
         exactly that variant */
      if (purchaseArea) purchaseArea.hidden = !available;
      if (soldoutPanel) {
        soldoutPanel.hidden = available;
        if (!available) this.updateNotify(soldoutPanel, variant);
      }
      if (qtyBlock) qtyBlock.classList.toggle('product-info__quantity--disabled', !available);

      if (available) {
        if (idInput) idInput.value = variant.id;
        if (button) {
          button.removeAttribute('disabled');
          button.removeAttribute('aria-disabled');
        }
        if (dynamic) dynamic.hidden = false;
        if (stickyBtn) {
          stickyBtn.removeAttribute('disabled');
          stickyBtn.removeAttribute('aria-disabled');
          stickyBtn.textContent = strings.addToCart || 'Add to cart';
          stickyBtn.hidden = false;
        }
      } else {
        if (dynamic) dynamic.hidden = true;
        // the sticky bar can't add an unavailable variant — hide it; the
        // notify panel above is the call to action
        if (stickyBtn) stickyBtn.hidden = true;
      }

      // an invalid combination has no variant → nothing more to price
      if (!variant) return;

      // keep the Shop Pay Installments amount in sync with the selected variant
      const terms = scope.querySelector('shopify-payment-terms');
      if (terms) terms.setAttribute('variant-id', variant.id);
      const termsIdInput = scope.querySelector('[data-terms-variant-id]');
      if (termsIdInput) termsIdInput.value = variant.id;

      const priceHtml = (el) => {
        if (!el) return;
        const current = el.querySelector('.price__current');
        const compare = el.querySelector('.price__compare');
        const priceRoot = el.querySelector('.price');
        if (current) {
          const label = current.querySelector('.visually-hidden');
          current.textContent = '';
          if (label) current.appendChild(label);
          current.appendChild(document.createTextNode(money(variant.price)));
        }
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        if (priceRoot) priceRoot.classList.toggle('price--on-sale', !!onSale);
        if (compare) {
          compare.hidden = !onSale;
          const amount = compare.querySelector('[data-compare-amount]');
          if (onSale && amount) amount.textContent = money(variant.compare_at_price);
        }
        const savingBadge = el.querySelector('[data-saving-badge]');
        if (savingBadge) {
          savingBadge.hidden = !onSale;
          if (onSale) {
            const template = savingBadge.dataset.savingTemplate || 'Save [amount]';
            savingBadge.textContent = template.replace('[amount]', money(variant.compare_at_price - variant.price));
          }
        }
      };
      priceHtml(priceWrap);
      priceHtml(stickyPrice);
    }

    /* Point the notify panel at the exact variant the shopper is asking about,
       so the customer we create is tagged for that colour/size specifically. */
    updateNotify(panel, variant) {
      // point the Klaviyo back-in-stock form at the exact variant selected
      const wrap = panel.querySelector('[data-bis-form]');
      if (wrap && variant) wrap.dataset.bisVariant = variant.id;
      const label = panel.querySelector('[data-soldout-variant]');
      // "Default Title" is Shopify's placeholder for single-variant products
      if (label) label.textContent = variant && variant.title && variant.title !== 'Default Title' ? variant.title : '';

      // the Fast Bundle upsell injects above the buy box — keep the notify
      // panel above the bundle, not stranded below it
      const bundle = document.querySelector('[id^="rbr-bundle-"]');
      if (bundle && bundle.parentElement && getComputedStyle(bundle).display !== 'none' && bundle.previousElementSibling !== panel) {
        bundle.parentElement.insertBefore(panel, bundle);
      }
    }

    updateMedia(variant) {
      if (!variant.featured_media_id) return;
      // scope to this picker's section, like updateBuyArea
      const scope = this.closest('.shopify-section') || document;
      const gallery = scope.querySelector('media-gallery');
      if (!gallery) return;
      const id = String(variant.featured_media_id);
      // variant-picker can upgrade before <media-gallery> is defined (initial
      // page load) — showMedia would be undefined; wait for the definition
      if (typeof gallery.showMedia === 'function') gallery.showMedia(id);
      else customElements.whenDefined('media-gallery').then(() => gallery.showMedia(id));
    }

    updateUrl(variant) {
      if (this.dataset.updateUrl === 'false' || !window.history.replaceState) return;
      const url = new URL(this.dataset.url || window.location.pathname, window.location.origin);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ---------- <media-gallery> ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('[data-gallery-stage]');
      this.slides = [...this.querySelectorAll('.gallery__slide')];
      this.thumbs = [...this.querySelectorAll('[data-thumb-media-id]')];
      this.dots = [...this.querySelectorAll('[data-dot-index]')];
      this.counter = this.querySelector('[data-gallery-counter]');
      this.zoomModal = this.querySelector('modal-dialog');

      this.thumbs.forEach((t) => t.addEventListener('click', () => this.showMedia(t.dataset.thumbMediaId)));
      this.dots.forEach((d) => d.addEventListener('click', () => this.showIndex(Number(d.dataset.dotIndex))));
      this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.showRelative(-1));
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.showRelative(1));
      this.querySelectorAll('[data-zoom-open]').forEach((z) =>
        z.addEventListener('click', () => {
          this.zoomModal?.show(z);
          this.zoomModal?.querySelector(`[data-zoom-index="${z.dataset.mediaIndex}"]`)?.scrollIntoView({ block: 'nearest', inline: 'center' });
        })
      );

      /* hidden slides are display:none, so their lazy images never fetch
         until shown — warm the neighborhood so arrow/thumb switches are
         instant without downloading all (sometimes 50+) media up front */
      const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 300));
      idle(() => this.preloadAround(this.slides.findIndex((s) => s.classList.contains('is-active')) + 1 || 1));

      /* mobile: track active slide while swiping */
      this.stage?.addEventListener(
        'scroll',
        window.MBTheme.debounce(() => {
          if (!this.isCarousel()) return;
          const index = Math.round(this.stage.scrollLeft / this.stage.clientWidth) + 1;
          this.markActive(this.slides[index - 1]?.dataset.mediaId, false);
        }, 80),
        { passive: true }
      );
    }

    isCarousel() {
      return window.matchMedia('(max-width: 989px)').matches;
    }

    showMedia(mediaId) {
      const slide = this.slides.find((s) => s.dataset.mediaId === String(mediaId));
      if (!slide) return;
      if (this.isCarousel()) {
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.stage.scrollTo({ left: slide.offsetLeft, behavior: reduced ? 'auto' : 'smooth' });
      }
      this.markActive(String(mediaId), true);
    }

    showIndex(index) {
      const slide = this.slides[index - 1];
      if (slide) this.showMedia(slide.dataset.mediaId);
    }

    showRelative(delta) {
      const current = this.slides.findIndex((s) => s.classList.contains('is-active'));
      const n = this.slides.length;
      if (!n) return;
      this.showIndex(((current + delta + n) % n) + 1); /* wraps at both ends */
    }

    preloadAround(index) {
      const n = this.slides.length;
      if (!n) return;
      [-2, -1, 0, 1, 2].forEach((d) => {
        const i = (((index - 1 + d) % n) + n) % n;
        this.slides[i]?.querySelectorAll('img[loading="lazy"]').forEach((img) => {
          img.loading = 'eager'; /* triggers the fetch even while display:none */
        });
      });
    }

    markActive(mediaId, moveDesktop) {
      if (!mediaId) return;
      this.slides.forEach((s) => s.classList.toggle('is-active', s.dataset.mediaId === mediaId));
      this.thumbs.forEach((t) => {
        const active = t.dataset.thumbMediaId === mediaId;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-current', active ? 'true' : 'false');
      });
      const activeIndex = this.slides.findIndex((s) => s.dataset.mediaId === mediaId) + 1;
      this.dots.forEach((d) => {
        const active = Number(d.dataset.dotIndex) === activeIndex;
        d.classList.toggle('is-active', active);
        if (active) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });
      if (this.counter) this.counter.textContent = `${activeIndex} / ${this.slides.length}`;
      const activeThumb = this.thumbs.find((t) => t.dataset.thumbMediaId === mediaId);
      activeThumb?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      this.preloadAround(activeIndex);
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /* ---------- <sticky-atc> (mobile) ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-sticky-submit]');
      this.button?.addEventListener('click', () => {
        document.querySelector('[data-atc-button]')?.click();
      });
      const buyBox = document.querySelector('.buy-buttons') || document.querySelector('.soldout-panel');
      if (!buyBox || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver(
        ([entry]) => {
          const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.classList.toggle('is-visible', show);
          this.setAttribute('aria-hidden', show ? 'false' : 'true');
        },
        { threshold: 0 }
      );
      io.observe(buyBox);
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ---------- recommendations (Shopify recommendations endpoint) ---------- */
  function initRecommendations() {
    document.querySelectorAll('[data-recommendations-url]').forEach(async (section) => {
      try {
        const response = await fetch(section.dataset.recommendationsUrl);
        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const fresh = doc.querySelector('[data-recommendations-url]');
        if (fresh && fresh.querySelector('.product-card')) {
          section.replaceWith(fresh);
        } else {
          section.closest('.shopify-section')?.remove();
        }
      } catch {
        section.closest('.shopify-section')?.remove();
      }
    });
  }

  /* ---------- recently viewed ---------- */
  const RV_KEY = 'mb:recently-viewed:v2'; // version bump invalidates stale card markup
  const RV_MAX_AGE = 7 * 24 * 60 * 60 * 1000; // stored cards freeze price/availability — expire them

  function storeRecentlyViewed() {
    const tpl = document.querySelector('template[data-rv-card]');
    if (!tpl || !productData) return;
    try {
      const list = JSON.parse(localStorage.getItem(RV_KEY) || '[]').filter((e) => e.handle !== productData.handle && Date.now() - (e.ts || 0) < RV_MAX_AGE);
      list.unshift({ handle: productData.handle, html: tpl.innerHTML, ts: Date.now() });
      localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, 10)));
    } catch {
      /* storage unavailable */
    }
  }

  function renderRecentlyViewed() {
    const host = document.querySelector('[data-recently-viewed-grid]');
    if (!host) return;
    try {
      const list = JSON.parse(localStorage.getItem(RV_KEY) || '[]').filter((e) => e.handle !== productData?.handle && Date.now() - (e.ts || 0) < RV_MAX_AGE);
      if (!list.length) {
        host.closest('.shopify-section')?.remove();
        return;
      }
      list.slice(0, Number(host.dataset.limit) || 8).forEach((entry) => {
        const cell = document.createElement('div');
        cell.innerHTML = entry.html; // self-origin HTML stored from our own rendered template
        host.appendChild(cell.firstElementChild || cell);
      });
    } catch {
      host.closest('.shopify-section')?.remove();
    }
  }

  const boot = () => {
    initRecommendations();
    storeRecentlyViewed();
    renderRecentlyViewed();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
